import { useEffect, useState } from 'react';
import { Modal, Select, message } from 'antd';
import { AddDesignationIcon, CloseIcon } from '../../../assets/icons';

/* ------------------------------------------------------------------ *
 * Add Designation modal (Create Government Department, Figma 1673-2200).
 * Collects a post's title, numeric rank, territory level and terminal flag.
 * Escalation is derived automatically from rank, so there's no field for it.
 * ------------------------------------------------------------------ */

/** Territory-level pill palettes (shared with the hierarchy table). */
export type Tone = 'state' | 'district' | 'subdivision' | 'tahsil' | 'default';

export const LEVEL_TONES: Record<Tone, string> = {
  state: 'bg-[#faf5ff] border-[#e9d5ff] text-[#7e22ce]',
  district: 'bg-brand-shade border-[#d1e2ff] text-brand',
  subdivision: 'bg-[#fffbeb] border-[#fde68a] text-[#b45309]',
  tahsil: 'bg-[#ecfdf5] border-[#a7f3d0] text-[#047857]',
  default: 'bg-slate-50 border-line-soft text-ink-secondary',
};

/** Selectable territory levels, each mapped to its pill tone. */
export const TERRITORY_LEVELS: { label: string; tone: Tone }[] = [
  { label: 'State Level', tone: 'state' },
  { label: 'District Level', tone: 'district' },
  { label: 'Sub-division Level', tone: 'subdivision' },
  { label: 'Tahsil Level', tone: 'tahsil' },
];

export interface NewDesignation {
  name: string;
  rank: number;
  level: string;
  tone: Tone;
  terminal: boolean;
}

/** Design-matched field styling (slate-300 border, soft shadow, focus ring). */
const INPUT =
  'w-full rounded-lg border border-[#cbd5e1] bg-white px-[15px] py-[11px] text-sm text-ink shadow-card placeholder:text-[#94a3b8] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

/** Uppercase field label with optional required marker. */
function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold uppercase tracking-[0.6px] text-[#334155]">
      {children}
      {required && <span className="font-semibold text-[#f43f5e]"> *</span>}
    </label>
  );
}

export default function AddDesignationModal({
  open,
  defaultRank,
  onCancel,
  onAdd,
}: {
  open: boolean;
  /** Pre-filled rank (next seniority slot) when the modal opens. */
  defaultRank: number;
  onCancel: () => void;
  onAdd: (designation: NewDesignation) => void;
}) {
  const [name, setName] = useState('');
  const [rank, setRank] = useState(String(defaultRank));
  const [level, setLevel] = useState(TERRITORY_LEVELS[0].label);
  const [terminal, setTerminal] = useState(false);

  // Reset to a fresh draft each time the modal opens.
  useEffect(() => {
    if (open) {
      setName('');
      setRank(String(defaultRank));
      setLevel(TERRITORY_LEVELS[0].label);
      setTerminal(false);
    }
  }, [open, defaultRank]);

  const submit = () => {
    if (!name.trim()) {
      message.error('Designation name is required.');
      return;
    }
    const parsed = parseInt(rank, 10);
    const tone = TERRITORY_LEVELS.find((l) => l.label === level)?.tone ?? 'default';
    onAdd({
      name: name.trim(),
      rank: Number.isFinite(parsed) && parsed > 0 ? parsed : defaultRank,
      level,
      tone,
      terminal,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      centered
      width={576}
      destroyOnHidden
      styles={{
        container: { padding: 0, borderRadius: 16, overflow: 'hidden' },
        body: { padding: 0 },
        mask: { background: 'rgba(11,28,48,0.2)', backdropFilter: 'blur(2px)' },
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-[#f1f5f9] px-6 pb-[17px] pt-6">
        <div className="flex flex-col gap-1 pr-6">
          <div className="flex items-center gap-2">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#eff6ff] text-brand">
              <AddDesignationIcon className="size-4" />
            </span>
            <h2 className="text-xl font-bold tracking-[-0.5px] text-[#0f172a]">Add Designation</h2>
          </div>
          <p className="pl-10 text-xs leading-[19.5px] text-[#64748b]">
            Define designation title, numeric rank, territory level, and status.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="rounded-lg p-1.5 text-[#94a3b8] transition-colors hover:bg-slate-100 hover:text-ink"
        >
          <CloseIcon className="size-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-5 px-6 py-5">
        {/* Designation name */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <FieldLabel required>Designation Name</FieldLabel>
            <span className="text-[11px] leading-4 text-[#94a3b8]">Official post designation</span>
          </div>
          <input
            className={INPUT}
            placeholder="e.g. Revenue Inspector / Naib Tahsildar"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        {/* Rank + Territory level */}
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <FieldLabel required>Rank</FieldLabel>
              <span className="rounded bg-[#eff6ff] px-1.5 py-0.5 text-[10px] font-medium leading-[15px] text-brand">
                Seniority Order
              </span>
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[#94a3b8]">
                #
              </span>
              <input
                type="number"
                min={1}
                className={[INPUT, 'pl-7 font-semibold text-[#1e293b]'].join(' ')}
                value={rank}
                onChange={(e) => setRank(e.target.value)}
              />
            </div>
            <p className="text-[11px] leading-[13.75px] text-[#64748b]">
              Lower number indicates higher seniority. Rank 1 is Apex Post.
            </p>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <FieldLabel required>Territory Level</FieldLabel>
            <Select
              className="w-full"
              size="large"
              value={level}
              onChange={setLevel}
              options={TERRITORY_LEVELS.map((l) => ({ value: l.label, label: l.label }))}
            />
            <p className="text-[11px] leading-[13.75px] text-[#64748b]">
              Jurisdictional boundary of this post.
            </p>
          </div>
        </div>

        {/* Lowest level / terminal post toggle */}
        <div className="flex items-center justify-between gap-4 rounded-xl border border-[rgba(226,232,240,0.9)] bg-[#f8fafc] p-[15px]">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#1e293b]">
                Lowest Level / Terminal Post
              </span>
              <span className="rounded-full bg-[#d1fae5] px-2 py-0.5 text-[10px] font-semibold leading-[15px] text-[#065f46]">
                Hierarchy Base
              </span>
            </div>
            <p className="text-xs leading-4 text-[#64748b]">
              Is this the lowest administrative post with no officers ranked below it?
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={terminal}
            onClick={() => setTerminal((v) => !v)}
            className={[
              'relative h-6 w-11 shrink-0 rounded-full transition-colors',
              terminal ? 'bg-[#059669]' : 'bg-[#c2c6d6]',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-0.5 size-5 rounded-full bg-white transition-all',
                terminal ? 'left-[22px]' : 'left-0.5',
              ].join(' ')}
            />
          </button>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 border-t border-[#f1f5f9] pt-1.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-[#cbd5e1] bg-white px-[17px] py-[11px] text-xs font-medium text-[#334155] shadow-card transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-lg bg-brand px-5 py-2.5 text-xs font-semibold text-white shadow-card transition-colors hover:bg-brand-dark"
          >
            Add Designation
          </button>
        </div>
      </div>
    </Modal>
  );
}
